import { isAddressComplete, mapAddressToDto } from '../address/helpers';
import { mapContactPersonsToDto } from '../contacts/helpers';
import { FeedbackType, OrderStatus, } from './models';
const FEEDBACK_TYPE_TO_DTO = {
    [FeedbackType.Change]: 'change',
    [FeedbackType.Complaint]: 'complaint',
    [FeedbackType.Feedback]: 'feedback',
};
const SALES_ID_LENGTH = 8;
export function mapOrderStatusFromDto(status) {
    return OrderStatus[status];
}
export function mapOrderFromDto(dto) {
    return {
        _dto: dto,
        id: dto.id,
        salesId: dto.id.slice(0, SALES_ID_LENGTH),
        createdDate: new Date(dto.createdAt),
        status: mapOrderStatusFromDto(dto.status),
        currency: dto.currency,
        reference: dto.case.reference,
        caseId: dto.case.referenceId,
        branchId: dto.branch.id,
        branchName: dto.branch.name,
        // `dto.totalPrice` is intentionally not read — it is always 0.
    };
}
/**
 * `GET /api/case/{caseId}/deliveries` (no `includeAssets`) gives only `id`/`reference`/`createdAt`
 * per order — no status, currency or branch. Everything else on `IOrder` stays `undefined`; see the
 * comment on that model. `caseId` is the one thing this read confirms, since it's the parameter.
 */
export function mapCaseOrderFromDeliveryDto(dto, caseId) {
    return {
        id: dto.id,
        salesId: dto.id.slice(0, SALES_ID_LENGTH),
        createdDate: new Date(dto.createdAt),
        reference: dto.reference,
        caseId,
    };
}
/**
 * `GET /api/orders/{orderId}/contacts`. Efkt's `types` is a set of roles on this one order; datahub's
 * `IContactPerson.type` is a single string, so multiple roles join into one display string rather
 * than picking (and silently dropping) just the first.
 */
export function mapOrderContactFromDto(dto) {
    return {
        type: dto.types.join(', '),
        name: `${dto.firstName} ${dto.lastName}`.trim(),
        email: dto.email,
        phone: dto.businessPhone,
        contactInformationId: dto.id,
    };
}
export function mapPhotographerInstructionFromDto(dto) {
    return {
        content: dto.content,
    };
}
export function mapOrderBookingFromDto(dto) {
    var _a;
    return {
        bookingStartsAt: dto.expectedArrivalAt ? new Date(dto.expectedArrivalAt) : undefined,
        bookingEndsAt: dto.expectedDepartureAt ? new Date(dto.expectedDepartureAt) : undefined,
        photographerName: (_a = dto.photographer) === null || _a === void 0 ? void 0 : _a.name,
        pickUpAddress: dto.address && {
            street: dto.address.street,
            zip: dto.address.zip,
            city: dto.address.city,
        },
        parentBookingId: dto.parentBookingId,
    };
}
/**
 * Every tri-state block goes out `{isUpdated: false}` — untouched — so this write can never wipe the
 * address/keyPickup/contacts/invoice a create-flow edit would otherwise guard with dirty-tracking.
 * See `IUpdateOrderAssignmentParams` for why `watcherContactIds` is sent even when empty, unlike
 * `mapUpdateOrderParamsToDto`.
 */
export function mapUpdateOrderAssignmentParamsToDto({ responsibleContactId, watcherContactIds, }) {
    return {
        address: { isUpdated: false },
        keyPickup: { isUpdated: false },
        contactPeople: { isUpdated: false },
        invoice: { isUpdated: false },
        ...(responsibleContactId && { responsibleAgentIds: [responsibleContactId] }),
        ...(watcherContactIds && { notificationRecipients: watcherContactIds }),
    };
}
export function mapOrderLineFromDto(dto) {
    var _a, _b;
    return {
        id: dto.id,
        productName: dto.salesProductName,
        orderedQuantity: dto.quantity,
        price: dto.totalPrice,
        unitPrice: dto.unitPrice,
        createdAt: new Date(dto.createdAt),
        // EFKT-MOCK — see IOrderLine's field comments. No mapOrderStatusFromDto reuse here: the mock
        // dto field carries no real value to map, so a direct default is more honest than routing it
        // through the real-status mapper.
        deliveredQuantity: (_a = dto.deliveredQuantity) !== null && _a !== void 0 ? _a : 0,
        status: OrderStatus.Pending,
        deliveredAt: dto.deliveredAt ? new Date(dto.deliveredAt) : undefined,
        // EFKT-BE-GAP — resolves `false` for every Efkt order line, because the gateway's order-lines
        // read does not return this field at all (no `CanReceiveFeedback` anywhere in the backend).
        // Consequence: the Efkt tree cannot render the "give feedback" entry point on an Efkt-sourced
        // order line, so `/feedback` is currently only reachable for one by a hand-built or shared URL.
        // Same BE ticket as `OrdersApi.submitFeedback`; nothing to change here once the field lands.
        canReceiveFeedback: (_b = dto.canReceiveFeedback) !== null && _b !== void 0 ? _b : false,
    };
}
/**
 * EFKT-BE-GAP — see `OrdersApi.submitFeedback`. Mirrors datahub's `mapFeedbackParamsToDto`: same
 * `type` union (`change`/`complaint`/`feedback`), and the per-drawing comments appended to the comment
 * body the same way, because the assumption is that Efkt's endpoint will take datahub's shape.
 *
 * One intentional difference: datahub appends `\n\n` plus the joined comments unconditionally, so a
 * feedback with no drawings gets two trailing blank lines. This skips the join when there is nothing
 * to append. Output is byte-identical whenever any drawing exists.
 */
export function mapSubmitFeedbackParamsToDto({ orderLineId, type, comment, drawingComments, uploadedDocuments, }) {
    return {
        orderLineId,
        type: FEEDBACK_TYPE_TO_DTO[type],
        uploadedDocuments,
        comment: drawingComments.length ? `${comment}\n\n${drawingComments.join('\n')}` : comment,
    };
}
function mapInvoiceToDto(invoice) {
    if (!isAddressComplete(invoice.address))
        return undefined;
    const base = {
        // `type` first: System.Text.Json's polymorphic reader expects the discriminator at the head
        // of the object, and no test on the backend exercises an out-of-order one.
        address: mapAddressToDto(invoice.address),
        name: invoice.name || '',
        email: invoice.email || '',
        phone: invoice.phone || '',
    };
    return invoice.type === 'Business'
        ? {
            type: 'Business',
            ...base,
            ean: invoice.ean || '',
            cvr: invoice.cvr || '',
            customerReference: invoice.customerReference || '',
        }
        : {
            type: 'EndUser',
            ...base,
        };
}
/**
 * Builds the `PUT /api/order/{orderId}` body.
 *
 * The one rule that matters: `{ isUpdated: true }` with no `value` is a **delete**, so an untouched
 * block must go out as `{ isUpdated: false }` and never as an updated-but-empty one. Getting that
 * backwards silently drops a saved address, invoice or fee line.
 */
export function mapUpdateOrderParamsToDto({ address, property, keyPickupAddress, contactPersons, invoice, responsibleContactId, watcherContactIds, photographerComment, touched = {}, }) {
    const invoiceDto = touched.invoice && invoice ? mapInvoiceToDto(invoice) : undefined;
    return {
        address: touched.address && isAddressComplete(address)
            ? {
                isUpdated: true,
                value: {
                    ...mapAddressToDto(address),
                    type: (property === null || property === void 0 ? void 0 : property.type) || '',
                    livingArea: property === null || property === void 0 ? void 0 : property.livingArea,
                    basementArea: property === null || property === void 0 ? void 0 : property.basementArea,
                    floors: property === null || property === void 0 ? void 0 : property.floors,
                },
            }
            : { isUpdated: false },
        // The genuine wipe case: the user unticked the key-pickup checkbox, so the fee line and the
        // stored address should both go.
        keyPickup: touched.keyPickup
            ? isAddressComplete(keyPickupAddress)
                ? { isUpdated: true, value: mapAddressToDto(keyPickupAddress) }
                : { isUpdated: true }
            : { isUpdated: false },
        contactPeople: touched.contactPersons
            ? { isUpdated: true, value: mapContactPersonsToDto(contactPersons) }
            : { isUpdated: false },
        // Same shape: both invoice checkboxes off means delete whatever is stored.
        invoice: touched.invoice
            ? invoiceDto
                ? { isUpdated: true, value: invoiceDto }
                : { isUpdated: true }
            : { isUpdated: false },
        // No envelope on these three, so omitting them when clean is what protects them.
        ...(responsibleContactId && { responsibleAgentIds: [responsibleContactId] }),
        ...((watcherContactIds === null || watcherContactIds === void 0 ? void 0 : watcherContactIds.length) && { notificationRecipients: watcherContactIds }),
        ...(touched.photographerComment && {
            photographerNotes: { isUpdated: true, value: photographerComment || '' },
        }),
    };
}
/**
 * Pulls the per-problem detail out of a rejected `OrdersApi.submitOrder`.
 *
 * Only a 422 carries it: the selection cannot be produced, so retrying unchanged cannot help — the
 * selection has to change. Every other submit failure (404, the 429 CRM-rejection collapse, 5xx)
 * has no `problems[]`, hence the empty array.
 *
 * Returns the whole problem, not just `message`: the caller renders copy keyed on `code` with
 * `salesProductName` as the subject, because `message` is written in production vocabulary. See
 * `IOrderProblem`.
 */
export function getConfirmOrderProblems(error) {
    var _a, _b, _c;
    const { problems } = (_c = (_b = (_a = error) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.data) !== null && _c !== void 0 ? _c : {};
    if (!Array.isArray(problems))
        return [];
    return problems.filter(problem => !!(problem === null || problem === void 0 ? void 0 : problem.code) || !!(problem === null || problem === void 0 ? void 0 : problem.message));
}
